import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { quotesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, Search, Pencil, Trash2, FileSpreadsheet, ArrowRightLeft, CheckCircle2, Printer } from 'lucide-react';
import { toast } from 'sonner';

const QuotesListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSlave = user?.role === 'company_user';

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selected, setSelected] = useState(null);
  const [convertDialog, setConvertDialog] = useState(false);

  useEffect(() => { fetchList(); }, []);

  const fetchList = async (q = '') => {
    try {
      const r = await quotesApi.getAll({ search: q });
      setList(r.data);
    } catch (e) { toast.error('Errore caricamento preventivi'); }
    finally { setLoading(false); }
  };

  const onSearch = (e) => { setSearch(e.target.value); fetchList(e.target.value); };

  const handleDelete = async () => {
    try {
      await quotesApi.delete(selected.id);
      toast.success('Preventivo eliminato');
      setDeleteDialog(false);
      fetchList(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const handleConvert = async () => {
    try {
      const r = await quotesApi.convertToDdt(selected.id);
      toast.success(`Preventivo convertito in DDT ${r.data.number}-${r.data.year}`);
      setConvertDialog(false);
      fetchList(search);
      // naviga al DDT
      setTimeout(() => navigate(`/ddt/${r.data.id}`), 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore conversione');
    }
  };

  const fmtCur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

  return (
    <div data-testid="quotes-list-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">Preventivi</h1>
          <p className="text-muted-foreground text-sm">Crea preventivi e convertili in DDT al momento della consegna</p>
        </div>
        <Link to="/preventivi/new">
          <Button data-testid="new-quote-btn" className="btn-primary">
            <Plus className="w-4 h-4 mr-2" /> NUOVO PREVENTIVO
          </Button>
        </Link>
      </div>

      <Card className="card-swiss">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="search-quote-input" placeholder="Cerca per numero o cliente..." value={search} onChange={onSearch} className="pl-10" />
          </div>
        </CardContent>
      </Card>

      <Card className="card-swiss">
        <CardContent className="p-0">
          {loading ? <div className="p-8 text-center text-muted-foreground">Caricamento...</div> :
           list.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessun preventivo</p>
              <Link to="/preventivi/new">
                <Button className="mt-4 btn-primary">
                  <Plus className="w-4 h-4 mr-2" /> CREA IL PRIMO PREVENTIVO
                </Button>
              </Link>
            </div>
           ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header-swiss w-[120px]">N°</TableHead>
                  <TableHead className="table-header-swiss">Cliente</TableHead>
                  <TableHead className="table-header-swiss w-[110px]">Data</TableHead>
                  <TableHead className="table-header-swiss w-[120px]">Validità</TableHead>
                  <TableHead className="table-header-swiss w-[120px] text-right">Totale</TableHead>
                  <TableHead className="table-header-swiss w-[140px]">Stato</TableHead>
                  <TableHead className="table-header-swiss w-[140px]">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((q) => {
                  const isConverted = q.quote_status === 'converted';
                  return (
                  <TableRow key={q.id} className={`table-row-swiss ${isConverted ? 'bg-emerald-50/30' : ''}`}>
                    <TableCell className="table-cell-swiss font-['JetBrains_Mono'] font-medium">PR-{q.number}/{q.year}</TableCell>
                    <TableCell className="table-cell-swiss">{q.client_name || <span className="text-muted-foreground italic">— non selezionato —</span>}</TableCell>
                    <TableCell className="table-cell-swiss">{fmtDate(q.date)}</TableCell>
                    <TableCell className="table-cell-swiss text-muted-foreground">{q.valid_until ? fmtDate(q.valid_until) : '—'}</TableCell>
                    <TableCell className="table-cell-swiss text-right font-['JetBrains_Mono']">{fmtCur(q.total_amount)}</TableCell>
                    <TableCell className="table-cell-swiss">
                      {isConverted ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Convertito
                        </Badge>
                      ) : q.quote_status === 'rejected' ? (
                        <Badge variant="secondary">Rifiutato</Badge>
                      ) : (
                        <Badge variant="outline" className="border-amber-300 text-amber-700">Aperto</Badge>
                      )}
                    </TableCell>
                    <TableCell className="table-cell-swiss">
                      <div className="flex gap-1">
                        <Link to={`/preventivi/${q.id}/edit`}>
                          <Button data-testid={`edit-quote-${q.id}`} variant="ghost" size="icon" className="h-8 w-8" title={isConverted ? 'Visualizza' : 'Modifica'}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button
                          data-testid={`pdf-quote-${q.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={async () => {
                            try {
                              const r = await quotesApi.getPdf(q.id);
                              const blob = new Blob([r.data], { type: 'application/pdf' });
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              const safeTitle = (q.title || '').replace(/[^a-z0-9\-_]+/gi, '_').slice(0, 40);
                              a.download = `Preventivo_${q.number}-${q.year}${safeTitle ? '_' + safeTitle : ''}.pdf`;
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              window.URL.revokeObjectURL(url);
                            } catch (e) {
                              toast.error('Errore nella generazione PDF');
                            }
                          }}
                          className="h-8 w-8 text-blue-700 hover:bg-blue-50"
                          title="Stampa PDF"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        {!isConverted && (
                          <Button
                            data-testid={`convert-quote-${q.id}`}
                            variant="ghost"
                            size="icon"
                            onClick={() => { setSelected(q); setConvertDialog(true); }}
                            className="h-8 w-8 text-emerald-700 hover:bg-emerald-50"
                            title="Converti in DDT"
                          >
                            <ArrowRightLeft className="w-4 h-4" />
                          </Button>
                        )}
                        {!isSlave && (
                          <Button data-testid={`delete-quote-${q.id}`} variant="ghost" size="icon" onClick={() => { setSelected(q); setDeleteDialog(true); }} className="h-8 w-8 text-destructive" title="Elimina">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );})}
              </TableBody>
            </Table>
           )}
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina preventivo</AlertDialogTitle>
            <AlertDialogDescription>Eliminare il preventivo PR-{selected?.number}/{selected?.year}?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-quote-btn" onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={convertDialog} onOpenChange={setConvertDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-emerald-700" /> Converti in DDT</AlertDialogTitle>
            <AlertDialogDescription>
              Il preventivo PR-{selected?.number}/{selected?.year} verrà convertito in DDT.
              Verrà generato un nuovo numero DDT e <b>lo stock dei prodotti sarà decrementato</b>.
              Il preventivo resterà visibile come "Convertito" ma non più modificabile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-convert-quote-btn" onClick={handleConvert} className="bg-emerald-700 hover:bg-emerald-800">Converti in DDT</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default QuotesListPage;
